import { useEffect, useRef, useState } from 'react'

export type QueryState<T> =
  | { readonly status: 'loading'; readonly data: undefined; readonly error: undefined }
  | { readonly status: 'ready'; readonly data: T; readonly error: undefined }
  | { readonly status: 'error'; readonly data: undefined; readonly error: Error }

/**
 * A deliberately small async-read hook. It exists so screens declare *what*
 * they need, not how it is fetched — the same seam a real data client slots into.
 */
export function useQuery<T>(run: () => Promise<T>, deps: readonly unknown[]): QueryState<T> {
  const [state, setState] = useState<QueryState<T>>({
    status: 'loading',
    data: undefined,
    error: undefined,
  })
  const runRef = useRef(run)
  runRef.current = run

  useEffect(() => {
    let active = true
    setState({ status: 'loading', data: undefined, error: undefined })
    runRef
      .current()
      .then((data) => {
        if (active) setState({ status: 'ready', data, error: undefined })
      })
      .catch((cause: unknown) => {
        if (active)
          setState({
            status: 'error',
            data: undefined,
            error: cause instanceof Error ? cause : new Error(String(cause)),
          })
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return state
}
